import { useId, type InputHTMLAttributes } from 'react';
import styles from './TextField.module.css';

/** Eigenschaften des Textfelds: Standard-Input-Attribute plus Beschriftung, Hinweis und Fehler. */
interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  /** Sichtbare Beschriftung (zugleich Barrierefreiheits-Label). */
  label: string;
  /** Optionaler Hilfetext unter dem Feld. */
  hint?: string;
  /** Fehlermeldung; setzt `aria-invalid` und wird als Alert angezeigt. */
  error?: string;
}

/**
 * Beschriftetes Eingabefeld mit Hilfetext und Fehleranzeige. Label, Hinweis und Fehler sind
 * über `htmlFor`/`aria-describedby` mit dem Feld verbunden.
 * @param props Beschriftung, Hinweis, Fehler und Input-Attribute.
 */
export function TextField({ label, hint, error, className, ...input }: TextFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <input
        id={id}
        className={styles.input}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...input}
      />
      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className={styles.error}>
          {error}
        </p>
      )}
    </div>
  );
}
